<template>
  <ComponentHeader title="Classification Management" subtitle="Create and manage entity classifiers using AI-powered InfoTagTypes">
      <div class="relative">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search classifiers..."
          class="pl-10 pr-4 py-2 w-64 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-transparent"
        >
        <FontAwesomeIcon :icon="['fas', 'magnifying-glass']" class="absolute left-3 top-3 text-gray-400" />
      </div>
      <button
        @click="showAIGenerator = true"
        class="btn-secondary"
      >
        <FontAwesomeIcon :icon="['fas', 'wand-magic-sparkles']" />
        Generate with AI
      </button>
      <button
        @click="openCreateManual"
        class="btn-primary"
      >
        <FontAwesomeIcon :icon="['fas', 'plus']" />
        Create Manually
      </button>
    </ComponentHeader>

  <div class="classification-manager p-4 space-y-4">
    <!-- Section 1: Existing Classifiers -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200">
      <div class="p-6">
        <div v-if="loadingClassifiers" class="py-12 text-center">
          <FontAwesomeIcon :icon="['fas', 'spinner']" class="text-blue-600 text-3xl animate-spin" />
        </div>

        <div v-else-if="filteredClassifiers.length === 0" class="py-12 text-center">
          <FontAwesomeIcon :icon="['fas', 'inbox']" class="text-gray-400 text-5xl mb-3" />
          <p class="text-sm text-gray-600">
            {{ searchQuery ? 'No classifiers found matching your search' : 'No classifiers configured' }}
          </p>
        </div>

        <div v-else class="space-y-2">
          <div
            v-for="classifier in filteredClassifiers"
            :key="classifier.id"
            class="group relative bg-gray-50 rounded-lg border border-gray-200 hover:shadow-md transition-all duration-300 p-4"
          >
            <div class="flex items-center justify-between">
              <!-- Left: Name and Type -->
              <div class="flex-1 min-w-0">
                <div class="flex items-center space-x-3">
                  <h5 class="font-semibold text-gray-900 text-base">{{ classifier.name }}</h5>
                  <span class="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">
                    {{ getTargetEntityType(classifier) }}
                  </span>
                  <span v-if="hasWebSearch(classifier)" class="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded">
                    <FontAwesomeIcon :icon="['fas', 'globe']" class="mr-1" />
                    Web Search
                  </span>
                </div>

                <!-- Second row: Details -->
                <div class="flex items-center space-x-4 mt-2 text-sm text-gray-600">
                  <span>
                    <FontAwesomeIcon :icon="['fas', 'tags']" class="mr-1" />
                    {{ getTagCount(classifier) }} tags
                  </span>

                  <span v-if="getDataSources(classifier).length > 0" class="flex items-center space-x-1">
                    <span class="text-gray-500">Data:</span>
                    <span
                      v-for="source in getDataSources(classifier).slice(0, 3)"
                      :key="source"
                      class="px-1.5 py-0.5 text-xs bg-gray-200 text-gray-700 rounded"
                    >
                      {{ source }}
                    </span>
                    <span v-if="getDataSources(classifier).length > 3" class="text-xs text-gray-500">
                      +{{ getDataSources(classifier).length - 3 }} more
                    </span>
                  </span>

                  <span v-if="getContextInfoTagTypes(classifier).length > 0" class="text-xs text-gray-500">
                    <FontAwesomeIcon :icon="['fas', 'link']" class="mr-1" />
                    {{ getContextInfoTagTypes(classifier).length }} context classifiers
                  </span>
                </div>
              </div>

              <!-- Right: Actions and Meta -->
              <div class="flex items-center space-x-4 ml-4">
                <!-- Meta info -->
                <div class="text-xs text-gray-500 text-right">
                  <div>ID: {{ classifier.id }}</div>
                  <div>{{ formatDate(classifier.updated_at || classifier.created_at) }}</div>
                </div>

                <!-- Action buttons -->
                <div class="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    @click="editClassifier(classifier)"
                    class="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <FontAwesomeIcon :icon="['fas', 'pen-to-square']" />
                  </button>
                  <button
                    @click="testClassifier(classifier)"
                    class="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    title="Test"
                  >
                    <FontAwesomeIcon :icon="['fas', 'flask']" />
                  </button>
                  <button
                    @click="duplicateClassifier(classifier)"
                    class="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                    title="Duplicate"
                  >
                    <FontAwesomeIcon :icon="['fas', 'copy']" />
                  </button>
                  <button
                    @click="deleteClassifier(classifier)"
                    class="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <FontAwesomeIcon :icon="['fas', 'trash']" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Section 2: Testing Panel -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200">
      <div class="px-6 py-4 border-b border-gray-200">
        <div class="flex items-center justify-between">
          <div>
            <h4 class="text-lg font-semibold text-gray-900">Test Classification</h4>
            <p class="text-sm text-gray-600 mt-1">
              Test classifiers on entities to validate configuration
            </p>
          </div>
          <button
            v-if="testResults.length > 0"
            @click="clearTestResults"
            class="text-sm text-gray-600 hover:text-gray-800"
          >
            <FontAwesomeIcon :icon="['fas', 'xmark']" class="mr-1" />
            Clear Results
          </button>
        </div>
      </div>

      <div class="p-6">
        <!-- Test Configuration -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <!-- Entity Type Selection -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              1. Select Entity Type
            </label>
            <select
              v-model="testConfig.selectedEntityType"
              @change="onEntityTypeChange"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-transparent"
            >
              <option value="">Select entity type...</option>
              <option
                v-for="entityType in entityTypes"
                :key="entityType.id"
                :value="entityType.type_name"
              >
                {{ entityType.type_name }}
              </option>
            </select>
          </div>

          <!-- Entity Selection -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              2. Select Entities to Test
            </label>
            <select
              v-model="testConfig.selectedEntityIds"
              multiple
              size="5"
              :disabled="!testConfig.selectedEntityType || loadingEntities"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-transparent disabled:bg-gray-100"
            >
              <option v-if="loadingEntities" disabled>Loading entities...</option>
              <option v-else-if="availableEntities.length === 0" disabled>
                No entities found
              </option>
              <option
                v-for="entity in availableEntities"
                :key="entity.id"
                :value="entity.id"
              >
                {{ entity.name }}
              </option>
            </select>
            <p class="text-xs text-gray-500 mt-1">
              Hold Ctrl/Cmd to select multiple entities
            </p>
          </div>

          <!-- Classifier Selection -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              3. Select Classifiers to Test
            </label>
            <select
              v-model="testConfig.selectedClassifierIds"
              multiple
              size="5"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-transparent"
            >
              <option
                v-for="classifier in filteredClassifiersForTesting"
                :key="classifier.id"
                :value="classifier.id"
              >
                {{ classifier.name }}
              </option>
            </select>
            <p class="text-xs text-gray-500 mt-1">
              {{ testConfig.selectedEntityType ?
                `Showing classifiers for ${testConfig.selectedEntityType}` :
                'Select entity type first' }}
            </p>
          </div>
        </div>

        <!-- Test Options -->
        <div class="flex items-center justify-between mb-4">
          <label class="flex items-center">
            <input
              v-model="testConfig.persistResults"
              type="checkbox"
              class="rounded border-gray-300 text-blue-600 focus:ring-blue-dark mr-2"
            />
            <span class="text-sm text-gray-700">Save classification results</span>
          </label>

          <button
            @click="runTest"
            :disabled="!canRunTest || testRunning"
            class="btn-primary"
          >
            <FontAwesomeIcon v-if="testRunning" :icon="['fas', 'spinner']" class="animate-spin" />
            <FontAwesomeIcon v-else :icon="['fas', 'play']" />
            {{ testRunning ? 'Running...' : 'Run Test' }}
          </button>
        </div>

        <!-- Test Results -->
        <div v-if="testResults.length > 0" class="space-y-4">
          <div class="bg-primary-50 rounded-lg p-4 border border-blue-200">
            <h5 class="font-semibold text-gray-900 mb-2">Test Summary</h5>
            <div class="grid grid-cols-4 gap-4">
              <div>
                <p class="text-xs text-gray-600">Total Tests</p>
                <p class="text-2xl font-bold text-gray-900">{{ testSummary.total_classifications }}</p>
              </div>
              <div>
                <p class="text-xs text-gray-600">Successful</p>
                <p class="text-2xl font-bold text-green-600">{{ testSummary.successful }}</p>
              </div>
              <div>
                <p class="text-xs text-gray-600">Avg Confidence</p>
                <p class="text-2xl font-bold text-blue-600">{{ (testSummary.average_confidence * 100).toFixed(0) }}%</p>
              </div>
              <div>
                <p class="text-xs text-gray-600">Total Time</p>
                <p class="text-2xl font-bold text-purple-600">{{ (testSummary.total_execution_time_ms / 1000).toFixed(1) }}s</p>
              </div>
            </div>
          </div>

          <!-- Individual Results -->
          <div
            v-for="result in testResults"
            :key="result.entity_id"
            class="border border-gray-200 rounded-lg"
          >
            <div class="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <h6 class="sensitive font-medium text-gray-900">
                {{ result.entity_name }}
                <span class="text-sm text-gray-500 ml-2">({{ result.entity_type }})</span>
              </h6>
            </div>

            <div class="p-4 space-y-3">
              <div
                v-for="classification in result.classifications"
                :key="classification.infotagtype_id"
                class="bg-white border border-gray-200 rounded-lg p-3"
              >
                <div class="flex items-start justify-between mb-2">
                  <div class="flex-1">
                    <p class="font-medium text-gray-900">{{ classification.infotagtype_name }}</p>
                    <p v-if="classification.selected_tag_name" class="text-sm text-gray-600 mt-1">
                      <FontAwesomeIcon :icon="['fas', 'tag']" class="mr-1" />
                      Selected: <span class="font-medium">{{ classification.selected_tag_name }}</span>
                    </p>
                    <p v-if="classification.error" class="text-sm text-red-600 mt-1">
                      <FontAwesomeIcon :icon="['fas', 'exclamation-circle']" class="mr-1" />
                      Error: {{ classification.error }}
                    </p>
                  </div>
                  <div class="flex items-center space-x-2">
                    <span
                      v-if="classification.confidence"
                      class="px-2 py-1 text-xs font-medium rounded"
                      :class="getConfidenceClass(classification.confidence)"
                    >
                      {{ (classification.confidence * 100).toFixed(0) }}%
                    </span>
                    <span class="text-xs text-gray-500">
                      {{ classification.execution_time_ms }}ms
                    </span>
                    <button
                      v-if="classification.reasoning"
                      @click="expandedClassifications[`${result.entity_id}-${classification.infotagtype_id}`] = !expandedClassifications[`${result.entity_id}-${classification.infotagtype_id}`]"
                      class="p-1 text-gray-500 hover:text-gray-700"
                    >
                      <FontAwesomeIcon :icon="expandedClassifications[`${result.entity_id}-${classification.infotagtype_id}`] ? ['fas', 'chevron-up'] : ['fas', 'chevron-down']" />
                    </button>
                  </div>
                </div>

                <!-- Expanded Details -->
                <div v-if="expandedClassifications[`${result.entity_id}-${classification.infotagtype_id}`]" class="mt-3 pt-3 border-t border-gray-200 space-y-3">
                  <!-- Reasoning -->
                  <div>
                    <p class="text-xs font-medium text-gray-700 mb-1">Reasoning:</p>
                    <p class="text-sm text-gray-600">{{ classification.reasoning }}</p>
                  </div>

                  <!-- Context Used -->
                  <div v-if="classification.context_used">
                    <p class="text-xs font-medium text-gray-700 mb-1">Context Used:</p>
                    <div class="bg-gray-50 rounded p-2 max-h-40 overflow-y-auto">
                      <pre class="text-xs text-gray-600">{{ formatContext(classification.context_used) }}</pre>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- AI Generator Modal -->
    <ClassifierAIGenerator
      :show="showAIGenerator"
      @close="showAIGenerator = false"
      @created="handleClassifierCreated"
    />

    <!-- Classifier Editor Modal -->
    <ClassifierEditor
      :show="showEditor"
      :classifier="editingClassifier"
      @close="showEditor = false"
      @saved="handleClassifierSaved"
    />
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import ClassifierAIGenerator from './ClassifierAIGenerator.vue';
import ClassifierEditor from './ClassifierEditor.vue';
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue';

export default {
  name: 'ClassificationManager',
  components: {
    FontAwesomeIcon,
    ClassifierAIGenerator,
    ClassifierEditor,
    ComponentHeader
  },
  data() {
    return {
      // Classifiers
      classifiers: [],
      classifierTagCounts: {}, // Store tag counts by InfoTagType ID
      loadingClassifiers: true,
      searchQuery: '',

      // Entity Types
      entityTypes: [],

      // Testing
      testConfig: {
        selectedEntityType: '',
        selectedEntityIds: [],
        selectedClassifierIds: [],
        persistResults: false
      },
      availableEntities: [],
      loadingEntities: false,
      testResults: [],
      testSummary: {},
      testRunning: false,
      expandedClassifications: {},

      // Modals
      showAIGenerator: false,
      showEditor: false,
      editingClassifier: null
    };
  },
  computed: {
    filteredClassifiers() {
      if (!this.searchQuery) return this.classifiers;

      const query = this.searchQuery.toLowerCase();
      return this.classifiers.filter(c =>
        c.name.toLowerCase().includes(query) ||
        c.id.toString().includes(query) ||
        this.getTargetEntityType(c).toLowerCase().includes(query)
      );
    },

    filteredClassifiersForTesting() {
      if (!this.testConfig.selectedEntityType) {
        return this.classifiers;
      }

      // Filter classifiers to only those matching the selected entity type
      return this.classifiers.filter(c => {
        const targetType = this.getTargetEntityType(c).toLowerCase();
        return targetType === this.testConfig.selectedEntityType.toLowerCase();
      });
    },

    canRunTest() {
      return this.testConfig.selectedEntityIds.length > 0 &&
             this.testConfig.selectedClassifierIds.length > 0;
    }
  },
  methods: {
    async fetchClassifiers() {
      try {
        const response = await axios.get('/api/infotagtypes/');
        // Filter to only those with classification_config
        this.classifiers = response.data.filter(itt =>
          itt.metadata && itt.metadata.classification_config
        );

        console.log('Loaded classifiers:', this.classifiers); // Debug log

        // Fetch tag counts for each classifier
        for (const classifier of this.classifiers) {
          await this.fetchTagCount(classifier.id);
        }
      } catch (error) {
        console.error('Error fetching classifiers:', error);
      } finally {
        this.loadingClassifiers = false;
      }
    },

    async fetchTagCount(infotagTypeId) {
      try {
        const response = await axios.get('/api/infotags/', {
          params: { infoTagType_id: infotagTypeId }
        });
        this.classifierTagCounts[infotagTypeId] = response.data.length;
      } catch (error) {
        console.error('Error fetching tag count:', error);
        this.classifierTagCounts[infotagTypeId] = 0;
      }
    },

    async fetchEntities() {
      if (!this.testConfig.selectedEntityType) {
        this.availableEntities = [];
        return;
      }

      this.loadingEntities = true;
      try {
        const response = await axios.get('/api/entities/', {
          params: {
            entity_type_id: this.getEntityTypeId(this.testConfig.selectedEntityType),
            limit: 500
          }
        });
        this.availableEntities = response.data.map(e => ({
          id: e.id,
          name: e.name,
          entity_type: e.entity_type_name
        }));
      } catch (error) {
        console.error('Error fetching entities:', error);
        this.availableEntities = [];
      } finally {
        this.loadingEntities = false;
      }
    },

    async fetchEntityTypes() {
      try {
        const response = await axios.get('/api/entity-types/');
        this.entityTypes = response.data;
      } catch (error) {
        console.error('Error fetching entity types:', error);
      }
    },

    getEntityTypeId(typeName) {
      const entityType = this.entityTypes.find(et =>
        et.type_name.toLowerCase() === typeName.toLowerCase()
      );
      return entityType ? entityType.id : null;
    },

    onEntityTypeChange() {
      // Reset selections when entity type changes
      this.testConfig.selectedEntityIds = [];
      this.testConfig.selectedClassifierIds = [];
      this.availableEntities = [];

      // Fetch entities for the selected type
      if (this.testConfig.selectedEntityType) {
        this.fetchEntities();
      }
    },

    async runTest() {
      this.testRunning = true;
      this.testResults = [];
      this.expandedClassifications = {};

      try {
        const response = await axios.post('/api/infotagtypes/test-classification/', {
          entity_ids: this.testConfig.selectedEntityIds,
          infotagtype_ids: this.testConfig.selectedClassifierIds,
          persist_results: this.testConfig.persistResults
        });

        if (response.data.success) {
          this.testResults = response.data.results;
          this.testSummary = response.data.summary;
        }
      } catch (error) {
        console.error('Error running test:', error);
        alert('Failed to run classification test');
      } finally {
        this.testRunning = false;
      }
    },

    clearTestResults() {
      this.testResults = [];
      this.testSummary = {};
      this.expandedClassifications = {};
    },

    editClassifier(classifier) {
      this.editingClassifier = classifier;
      this.showEditor = true;
    },

    testClassifier(classifier) {
      // Pre-select this classifier for testing
      this.testConfig.selectedClassifierIds = [classifier.id];
      // Scroll to testing section
      const testSection = document.querySelector('.bg-white.rounded-lg:nth-child(3)');
      if (testSection) {
        testSection.scrollIntoView({ behavior: 'smooth' });
      }
    },

    async duplicateClassifier(classifier) {
      if (!confirm(`Duplicate classifier "${classifier.name}"?`)) return;

      try {
        const newClassifier = {
          name: `${classifier.name} (Copy)`,
          UIColor: classifier.UIColor,
          metadata: JSON.parse(JSON.stringify(classifier.metadata))
        };

        await axios.post('/api/infotagtypes/', newClassifier);
        this.fetchClassifiers();
      } catch (error) {
        console.error('Error duplicating classifier:', error);
        alert('Failed to duplicate classifier');
      }
    },

    async deleteClassifier(classifier) {
      if (!confirm(`Delete classifier "${classifier.name}"? This will also delete all associated InfoTags and classifications.`)) return;

      try {
        await axios.delete(`/api/infotagtypes/${classifier.id}/`);
        this.fetchClassifiers();
      } catch (error) {
        console.error('Error deleting classifier:', error);
        alert('Failed to delete classifier');
      }
    },

    openCreateManual() {
      this.editingClassifier = null;
      this.showEditor = true;
    },

    handleClassifierCreated(newClassifier) {
      // Refresh the classifiers list
      this.fetchClassifiers();
    },

    handleClassifierSaved() {
      // Refresh the classifiers list
      this.fetchClassifiers();
    },

    // Helper methods
    getTargetEntityType(classifier) {
      return classifier.metadata?.classification_config?.target_entity_type || 'Unknown';
    },

    hasWebSearch(classifier) {
      return classifier.metadata?.classification_config?.enable_web_search || false;
    },

    getTagCount(classifier) {
      return this.classifierTagCounts[classifier.id] || 0;
    },

    getDataSources(classifier) {
      return classifier.metadata?.classification_config?.performance_data_sources || [];
    },

    getContextInfoTagTypes(classifier) {
      return classifier.metadata?.classification_config?.include_infotag_types || [];
    },

    getClassifierNameById(id) {
      const classifier = this.classifiers.find(c => c.id === id);
      return classifier ? classifier.name : `ID ${id}`;
    },

    getConfidenceClass(confidence) {
      if (confidence >= 0.8) return 'bg-green-100 text-green-800';
      if (confidence >= 0.6) return 'bg-yellow-100 text-yellow-800';
      return 'bg-orange-100 text-orange-800';
    },

    formatContext(context) {
      return JSON.stringify(context, null, 2);
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
  },
  mounted() {
    this.fetchClassifiers();
    this.fetchEntityTypes();
  }
};
</script>

<style scoped>
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
